import React, { useContext, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useParams, withRouter } from "react-router-dom";
import { Tooltip, Typography } from "@material-ui/core";
import LogoPrit from "~/assets/images/logo-prit-new-black.png";
import { AddCircle } from "@material-ui/icons";
import bookingTransactionService, { getBusinessBookingTransaction } from "~/services/bookingTransactionService";
import { mixpanelTrackEvent } from "~/utils/mixpanel";
import packagesServices from "~/services/packagesServices";
import paymentListServices from "~/services/paymentListServices";
import { DialogBuilder } from "~/utils/DialogProvider";
import { googleSignIn, googleSignOut, isSignedOnGoogle } from "~/utils/GoogleCalendar/GoogleCalendar";
import DialogText from "../common/dialog/dialogSucess";
import MuiStylesCardReservations from "./MuiStylesCardReservations";
import EmptyState from "./EmptyState";
import ListReservations from "./ListReservations";
import ListPackagesGrouped from "./ListPackagesGrouped";
import CardReservationSkeleton from "./CardReservationSkeleton";
import GoogleCalendarSyncCard from "./GoogleCalendarSyncCard";
import businessAction from "~/store/actions/businessAction";
import { useDispatch } from "react-redux";
import usePackagePaymentModal from "~/hooks/usePackagePaymentModal";
import { usePolicyCancellation } from "~/contexts/PolicyCancellationContext";
import ModalVerifyAccess from "~/components/common/ModalVerifyAccess";

const CardReservations = (props) => {
	const { setBusinessesList: setBusinessesListProps } = props;
	const classes = MuiStylesCardReservations();
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const { bookingTransactionId } = useParams();
	const { id } = useParams();
	const { business } = useSelector((store) => store.business);
	const { user } = useSelector((store) => store.auth);
	const { openPaymentModal } = usePackagePaymentModal();
	const { loadPolicyCancellation } = usePolicyCancellation();

	const [loading, setLoading] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [showLoadMore, setShowLoadMore] = useState(true);
	const [isUpcomingMode, setIsUpcomingMode] = useState(true);
	const [upcomingTotalCount, setUpcomingTotalCount] = useState(null);
	const [showLoadMorePackages, setShowLoadMorePackages] = useState(false);
	const [noMoreTooltipOpen, setNoMoreTooltipOpen] = useState(false);
	const [showReservations, setShowReservations] = useState("reservations");
	const [openPixConfirmation, setOpenPixConfirmation] = useState(false);
	const [reservations, setReservations] = useState([]);
	const [paymentList, setPaymentList] = useState([]);
	const [page, setPage] = useState(1);
	const [pagePackage, setPagePackage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [validationEnd, setValidationEnd] = useState(true);
	const [googleCalendarSync, setGoogleCalendarSync] = useState(false);
	const [showVerifyAccessModal, setShowVerifyAccessModal] = useState(false);
	const [pixConfirmation, setPixConfirmation] = useState(
		new URLSearchParams(props.location.search).get("pixConfirmation")
	);
	const userId = localStorage.getItem("@prit-web/userId");
	const language = sessionStorage.getItem("@prit-web/language") ?? business?.language;
	const [packages, setPackages] = useState([]);
	const [packageFilter, setPackageFilter] = useState("all");
	const [selectedBusiness, setSelectedBusiness] = useState(null);
	const [businessesList, setBusinessesList] = useState([]);
	const [businessesMap, setBusinessesMap] = useState(new Map());
	const [businessesPolicyCancellation, setBusinessesPolicyCancellation ] = useState([]);
	const isMountedRef = useRef(true);

	const dialog = useContext(DialogBuilder);

	const bookingStatusTypes = {
		active: t("active"),
		inactive: t("inactive"),
		cancelled: t("cancelled"),
		completed: t("completed"),
		pending_accept: t("pending_accept"),
		refuse: t("refuse"),
		accept: t("accept"),
		waiting: t("waiting"),
	};

	const cancellationReasonTypes = {
		client: t("client"),
		business: t("business"),
		lack_payment: t("lack_payment"),
	};

	const status = (selectedReservation) => {
		if (selectedReservation.status === "cancelled") {
			return cancellationReasonTypes[selectedReservation.cancellationReason];
		}
		if (selectedReservation.status === "active" && selectedReservation.confirmReservation) {
			return t("Confirmed");
		}
		return bookingStatusTypes[selectedReservation.status];
	};

	const fetchReservations = (businessId, pageToFetch = page, upcoming = false) => {
		if (pageToFetch === 1) {
			setLoading(true);
		} else {
			setLoadingMore(true);
		}
		if (!userId && props?.user?.isAnonymous) {
			setLoading(false);
			localStorage.setItem("goBackRoute", JSON.stringify("/reservas/" + business.id));
			props.history.push(`/business/link/${business.id}`, { goBackRoute: "/reservas/" + business.id, goBackRouteProps: props.location.state });
			return;
		}
		getBusinessBookingTransaction(userId, businessId, language, pageToFetch, upcoming)
			.then((res) => {
				const totalPagesReservations = parseInt(res?.headers?.["x-total-pages"]);
				setTotalPages(totalPagesReservations);
				if (upcoming) {
					const totalCount = parseInt(res?.headers?.["x-total-count"]);
					if (!isNaN(totalCount)) {
						setUpcomingTotalCount(totalCount);
					}
				}
				let bookingTransaction = res.data;
				let services = [];
				if (pageToFetch === 1) {
					setReservations([]);
				}

				setBusinessesMap((prevMap) => {
					let hasNew = false;
					const newMap = new Map(prevMap);
					bookingTransaction.forEach((transaction) => {
						transaction.bookings.forEach((booking) => {
							if (booking.business?.id && !newMap.has(booking.business.id)) {
								newMap.set(booking.business.id, {
									id: booking.business.id,
									name: booking.business.name || booking.business.businessName || "",
								});
								hasNew = true;
							}
						});
					});
					if (hasNew) {
						const businessesArray = Array.from(newMap.values());
						setBusinessesList(businessesArray);
						if (setBusinessesListProps) {
							setTimeout(() => setBusinessesListProps(businessesArray), 0);
						}
						return newMap;
					}
					return prevMap;
				});
				bookingTransaction.forEach((transaction) => {
					transaction.bookings
					.filter((booking) => booking?.business)
					.forEach((booking) => {
						let service = {
							...booking.service,
							bookingId: booking.id,
							bookingTransactionId: transaction.id,
							business: booking.business,
							professional: booking.professional.name,
							avatarUrlProfessional: booking.professional.avatarUrl,
							price: booking.price,
							pricingType: booking.pricingType,
							startTime: booking.startTime,
							createdAt: booking.createdAt,
							endTime: booking.endTime,
							status: booking.status?.toLowerCase(),
							confirmReservation: booking.confirmReservation,
							cancellationReason: booking.cancellationReason,
							total: transaction.total,
							coupon: transaction.couponId,
							professionalId: booking.professional.id,
							serviceId: booking.service.id,
							businessId: booking.business.id,
							answer: booking.bookingAnswers.length > 0 ? booking.bookingAnswers[0] : "",
							packageId: transaction.packageId,
							packageName: transaction.packageName,
						};
						services.push(service);
					});
				});

				setPage(pageToFetch);
				setShowLoadMore(pageToFetch < totalPagesReservations);

				setReservations((prev) => [...prev, ...services]);
				if (services.length === 0 && !business?.id) {
					props.history.push(`/sem-reservas`);
				}
				getPaymentLists(services.map((e) => e.bookingTransactionId));
			})
			.catch((err) => {
				if (err.response.status === 401 || err.response.status === 403) {
					localStorage.setItem("goBackRoute", JSON.stringify("/reservas/" + business.id));
					props.history.push(`/business/link/${business.id}`, {
						goBackRoute: "/reservas/" + business.id,
						goBackRouteProps: props.location.state,
					});
				}
			})
			.finally(() => {
				setLoading(false);
				setLoadingMore(false);
			});
	};

	const getPaymentLists = (ids) => {
		paymentListServices
			.postPaymentListServices({
				booking_transaction_ids: ids,
			})
			.then(({ data }) => {
				if (isMountedRef.current) {
					setPaymentList([...paymentList, ...data]);
				}
			});
	};

	useEffect(() => {
		if (business !== null && page === 1 && !loading && validationEnd) {
			fetchReservations(selectedBusiness, 1, true);
			setValidationEnd(false);
		}
	}, [business]);

	useEffect(() => {
		if (reservations?.length > 0 && pixConfirmation) {
			setOpenPixConfirmation(true);
		}
	}, [reservations]);

	const handleGoogleCalendarSyncSucess = () => {
		if (isMountedRef.current) {
			setGoogleCalendarSync(true);
		}
	};

	const handleGoogleAgendaSave = () => {
		if (!googleCalendarSync) {
			googleSignIn(handleGoogleCalendarSyncSucess);
		} else {
			googleSignOut();
			setGoogleCalendarSync(false);
		}
	};

	const loadDetails = async (bookingTransactionId) => {
		bookingTransactionService
			.getOneBookingTransaction(bookingTransactionId)
			.then((res) => {
				let transaction = res.data;
				let services = [];
				transaction.bookings.forEach((booking) => {
					let service = {
						...booking.service,
						bookingId: booking.id,
						bookingTransactionId: transaction.id,
						business: booking.business,
						professional: booking.professional.name,
						avatarUrlProfessional: booking.professional.avatarUrl,
						price: booking.price,
						pricingType: booking.pricingType,
						startTime: booking.startTime,
						createdAt: booking.createdAt,
						endTime: booking.endTime,
						status: booking.status?.toLowerCase(),
						confirmReservation: booking.confirmReservation,
						cancellationReason: booking.cancellationReason,
						total: transaction.total,
						coupon: transaction.couponId,
						professionalId: booking.professional.id,
						serviceId: booking.service.id,
						businessId: booking.business.id,
						answer: booking.bookingAnswers.length > 0 ? booking.bookingAnswers[0] : "",
					};
					services.push(service);
				});
				handleClickOpen(services[0]);
			})
			.catch((err) => {
				setLoading(false);
				
				if (err?.response?.status === 403) {
					const errorCode = err?.response?.data?.code || "";
					const errorMessage = err?.response?.data?.message || "";
					const businessId = id || business?.id || props.location.state?.goBackRouteProps?.businessId;

					if (errorCode === "booking_owner_mismatch") {
						mixpanelTrackEvent("Acesso_Negado_Reserva", {
							User_Id_Booking: bookingTransactionId,
							Business_Id: businessId || null,
							User_Id: user?.id || userId,
							tela: "Lista_Reservas",
						}, user || {}, {});
						setShowVerifyAccessModal(true);
						return;
					}

					if (errorMessage.includes("not authorized") || errorMessage.includes("unauthorized") || errorMessage === "You are not authorized to access this page.") {
						mixpanelTrackEvent("Usuario_Sem_Permissao_Reserva", {
							User_Id_Booking: bookingTransactionId,
							Business_Id: businessId || null,
							User_Id: user?.id || userId,
						}, user || {}, {});

						if (businessId) {
							props.history.push(`/business/link`, {
								linkReserveNotAuthorized: true,
								businessId: businessId || null
							});
							return;
						}
					}
				}
				
				if (err?.response?.status === 401) {
					localStorage.setItem("goBackRoute", JSON.stringify(window.location.pathname));
					props.history.push(`/business/link/${business.id}`, {
						goBackRoute: window.location.pathname,
						goBackRouteProps: props.location.state,
					});
					return;
				}
				
				// Outros erros
				dialog
					.withMessage(`${err?.response?.data?.message || t("An unexpected error occurred, ")} ${t("Try again!")}`)
					.show();
			});
	};

	const fetchPackagesScheduled = (fetchPage = 1, perPage = 10, filter = packageFilter, bizId = selectedBusiness || business?.id) => {
		if (!bizId) return;
		if (fetchPage === 1) {
			setLoading(true);
		} else {
			setLoadingMore(true);
		}
		packagesServices
			.packageBookings(bizId, fetchPage, perPage, language, filter)
			.then((res) => {
				const data = res.data || [];
				const totalCount = parseInt(res.headers["x-total-count"] || "0");
				const totalPages = parseInt(res.headers["x-total-page"] || res.headers["x-total-pages"] || Math.ceil(totalCount / perPage) || "1");
				setPackages((prev) => fetchPage === 1 ? data : [...prev, ...data]);
				getPaymentLists(data.map((g) => g.bookingTransactionId));
				setShowLoadMorePackages(fetchPage < totalPages);
			})
			.catch((error) => {
				console.error("Erro ao buscar pacotes:", error);
			})
			.finally(() => {
				setLoading(false);
				setLoadingMore(false);
			});
	};

	useEffect(() => {
		if (bookingTransactionId) {
			loadDetails(bookingTransactionId);
		}
	}, [bookingTransactionId]);

	const handleTabChange = async (tab) => {
		if (loading) return;

		setReservations([]);
		setPackages([]);

		switch (tab) {
			case "packages":
				setShowReservations("packages");
				setPagePackage(1);
				setPackageFilter("all");
				fetchPackagesScheduled(1, 10, "all", selectedBusiness || business?.id);
				break;
			case "reservations":
				setShowReservations("reservations");
				setPage(1);
				setIsUpcomingMode(true);
				setUpcomingTotalCount(null);
				setValidationEnd(false);
				fetchReservations(selectedBusiness, 1, true);
				break;
			default:
				console.warn(t("Unknown tab:"), tab);
		}
	};

	const bookingPayment = (id) => {
		let payment = paymentList.filter((e) => e.booking_transaction_id === id);
		return payment.length > 0 ? payment[0].total_transactions : 0;
	};

	const bookingDiscount = (id) => {
		let payment = paymentList.filter((e) => e.booking_transaction_id === id);
		return payment.length > 0 ? payment[0].total_discount : 0;
	};

	const bookingPaymentRefund = (id) => {
		let payment = paymentList.filter((e) => e.booking_transaction_id === id);
		return payment.length > 0 ? payment[0].total_refunded : null;
	};

	const getPaymentMethod = (id) => {
		let payment = paymentList.filter((e) => e.booking_transaction_id === id);
		return payment.length > 0 ? payment[0].payments?.[0]?.payment_type : null;
	};

	const getPayment = (id, totalType) => {
		let payment = paymentList.filter((e) => e.booking_transaction_id === id);
		return payment.length > 0 ? payment[0]?.[totalType] : null;
	};

	const handleClickOpen = async (item) => {
		await dispatch(businessAction.getBusiness(item?.business?.id));
		const paymentMethod = getPaymentMethod(item.bookingTransactionId);
		const businessPolicy = businessesPolicyCancellation.find((p) => p.business_id === item?.business?.id);
		props.history.push(
			`/appointment-summary/${item?.business?.id}/bookingTransactionId/${item.bookingTransactionId}/bookingId/${item.bookingId}`,
			{
				paymentMethod,
				bookingPayment: bookingPayment(item.bookingTransactionId),
				isPolicyCancellation: businessPolicy?.isPolicyCancellation ?? false,
			}
		);
	};

	useEffect(() => {
		if (reservations?.length > 0 && pixConfirmation) {
			setOpenPixConfirmation(true);
		}
	}, [reservations]);


	useEffect(() => {
		isSignedOnGoogle(handleGoogleCalendarSyncSucess);
	}, []);

	useEffect(() => {
		if (showReservations === "packages") {
			setPagePackage(1);
			setPackages([]);
			fetchPackagesScheduled(1, 10, packageFilter, selectedBusiness || business?.id);
		}
	}, [packageFilter]);

	useEffect(() => {
		if (showReservations === "packages") {
			setPagePackage(1);
			setPackages([]);
			fetchPackagesScheduled(1, 10, packageFilter, selectedBusiness || business?.id);
		} else {
			setPage(1);
			setReservations([]);
			fetchReservations(selectedBusiness, 1, isUpcomingMode);
		}
	}, [selectedBusiness]);

	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	useEffect(() => {
		const callPolicyCancellation = async () => {
			try {
				if (businessesMap && businessesMap.size > 0) {
					const results = await Promise.all(
						Array.from(businessesMap.keys()).map(async (key) => {
							const res = await loadPolicyCancellation(
								key, 
								language || business?.language
							);
							return {
								business_id: key,
								isPolicyCancellation: res?.isPolicyCancellation ?? false,
							};
						})
					);
					setBusinessesPolicyCancellation(results);
				}
			} catch (error) {
				console.error(error);
			}
		};
		callPolicyCancellation();
	}, [businessesMap]);

	return (
		<>
			<script src="https://apis.google.com/js/api.js" type="text/javascript"></script>
			<div className={classes.headerWrapper}>
				<div className={classes.headerContainer} style={{ alignItems: "flex-start" }}>
					<img src={LogoPrit} alt="Prit" style={{ height: "36px", width: "auto", filter: "brightness(0) invert(1)", marginBottom: "12px" }} />
					<Typography className={classes.pageSubtitle}>{t("reservas_subtitle")}</Typography>
				</div>

				<div className={classes.tabsContainer}>
					<div className={classes.tabsSegmented}>
						<button
							onClick={() => handleTabChange("reservations")}
							className={`${classes.tab} ${showReservations === "reservations" ? "active" : ""}`}
							translate="no"
						>
							{t("reservas_tab_appointments")}
						</button>
						{(business?.id) && (
							<button
								onClick={() => handleTabChange("packages")}
								className={`${classes.tab} ${showReservations === "packages" ? "active" : ""}`}
								translate="no"
							>
								{t("reservas_tab_packages")}
							</button>
						)}
					</div>
				</div>
			</div>

			{/* Business filter chips */}
			{businessesList?.length > 1 && (
				<div style={{
					display: "flex",
					gap: 8,
					padding: "12px 16px",
					overflowX: "auto",
					backgroundColor: "#F6F6F3",
					scrollbarWidth: "none",
				}}>
					<button
						onClick={() => setSelectedBusiness(null)}
						style={{
							flexShrink: 0,
							padding: "6px 16px",
							borderRadius: 20,
							border: "none",
							backgroundColor: !selectedBusiness ? "#8C1837" : "#E8E8E8",
							color: !selectedBusiness ? "#fff" : "#555",
							fontSize: 13,
							fontWeight: 600,
							cursor: "pointer",
						}}>
						{t("reservas_filter_all")}
					</button>
					{businessesList.map((biz) => (
						<button
							key={biz.id}
							translate="no"
							onClick={() => setSelectedBusiness(biz.id === selectedBusiness ? null : biz.id)}
							style={{
								flexShrink: 0,
								padding: "6px 16px",
								borderRadius: 20,
								border: "none",
								backgroundColor: selectedBusiness === biz.id ? "#8C1837" : "#E8E8E8",
								color: selectedBusiness === biz.id ? "#fff" : "#555",
								fontSize: 13,
								fontWeight: 600,
								cursor: "pointer",
							}}>
							{biz.name}
						</button>
					))}
				</div>
			)}

			{(reservations?.length > 0 || packages?.length > 0 || googleCalendarSync) && (
				<GoogleCalendarSyncCard
					isSynced={googleCalendarSync}
					onToggleSync={handleGoogleAgendaSave}
				/>
			)}
			{showReservations === "reservations" ? (
				loading ? (
					<CardReservationSkeleton count={4} />
				) : (
				<div style={{ backgroundColor: "#F6F6F3", minHeight: "calc(100vh - 200px)" }}>
					{reservations?.length > 0 ? (
						<>
							<ListReservations
								reservations={reservations}
								handleClickOpen={handleClickOpen}
								bookingPayment={bookingPayment}
								bookingPaymentRefund={bookingPaymentRefund}
								status={status}
								setSelectedBusiness={setSelectedBusiness}
								selectedBusiness={selectedBusiness}
								showLoadMore={showLoadMore}
								business={business}
								isUpcomingMode={isUpcomingMode}
								upcomingTotalCount={upcomingTotalCount}
								onViewAll={() => {
									setIsUpcomingMode(false);
									setPage(1);
									setReservations([]);
									fetchReservations(selectedBusiness, 1, false);
								}}
								loadMoreReservations={() =>
								loadingMore ? (
									<CardReservationSkeleton count={2} />
								) : (
									<Tooltip
										open={noMoreTooltipOpen}
										title={t("No more appointments")}
										placement="top"
										arrow
									>
										<div
											style={{
												color: "#474747",
												display: "flex",
												justifyContent: "center",
												alignItems: "center",
												gap: "8px",
												cursor: showLoadMore ? "pointer" : "default",
												padding: "16px",
											}}
											onClick={() => {
												if (!showLoadMore) {
													setNoMoreTooltipOpen(true);
													setTimeout(() => setNoMoreTooltipOpen(false), 2000);
													return;
												}
												setPage((prevPage) => {
													const nextPage = prevPage + 1;
													fetchReservations(selectedBusiness, nextPage);
													return nextPage;
												});
											}}>
											<AddCircle />
											<Typography>{t("Load more")}</Typography>
										</div>
									</Tooltip>
								)
							}
							/>
						</>
					) : (
						<>
							{!loading && (
								<EmptyState
									title={t("You dont have any appointments yet")}
									subtitle={t("As soon as you make the first one it will appear here")}
									withScheduleInstructions={{
										firstText: t("Choose an available service and time"),
										secondText: t("Confirm the appointment with the professional"),
									}}
									buttonLabel={t("Schedule now")}
									handleClick={() => props.history.push(`/business/${business.id}`)}
								/>
							)}
						</>
					)}
				</div>
				)
			) : (
				loading && packages.length === 0 ? (
					<CardReservationSkeleton count={4} />
				) : (
				<div
					style={{
						backgroundColor: "#F6F6F3",
						paddingTop: packages && packages.length > 0 ? 0 : 8,
						paddingBottom: 60,
						minHeight: "calc(100vh - 200px)",
						boxSizing: "border-box",
					}}
				>
					{packages && packages.length > 0 ? (
						<ListPackagesGrouped
							packages={packages}
							business={business}
							businessesPolicyCancellation={businessesPolicyCancellation}
							onPayNow={(bookingTransactionId, totalPaid, title) =>
								openPaymentModal({
									bookingTransactionId,
									totalPaid,
									currency: business?.currency || "BRL",
									title,
									onSuccess: () => {
										setPagePackage(1);
										fetchPackagesScheduled(1, 10, "all");
									},
								})
							}
							onAgendar={(group, bizId) => {
								const targetId = bizId || business?.id;
								const unscheduled = group?.sessions?.find(s => s.stage === "to_schedule" || !s.startTime);
								if (!unscheduled) return;
								const remaining = group.totalSessions - group.scheduledSessions;
								props.history.push(`/selecionar-data/${targetId}`, {
									services: [{
										id: group.serviceId,
										name: group.serviceName,
										serviceName: group.serviceName,
										price: unscheduled.price,
										featureItems: (group.featureItems || []).map(f => f.id),
									}],
									bookingId: unscheduled.bookingId,
									reservationUpdate: true,
									isPackage: true,
									packageSessionInfo: {
										packageName: group.package?.name || group.serviceName,
										packageQuantity: group.totalSessions,
										packageRemainding: remaining,
										packageExpiry: group.sessions?.[0]?.expiresAt
											? group.sessions[0].expiresAt
											: null,
									},
								});
							}}
							loadMorePackages={showLoadMorePackages ? () => (
								loadingMore ? (
									<CardReservationSkeleton count={2} />
								) : (
									<div
										style={{ color: "#474747", display: "flex", justifyContent: "center", alignItems: "center", gap: "8px", padding: "20px", cursor: "pointer" }}
										onClick={() => {
											const next = pagePackage + 1;
											setPagePackage(next);
											fetchPackagesScheduled(next, 10, packageFilter, selectedBusiness || business?.id);
										}}>
										<AddCircle />
										<Typography>{t("Load more")}</Typography>
									</div>
								)
							) : null}
						/>
					) : (
						!loading && (
							<EmptyState
								title={t("You dont have any active packages yet")}
								subtitle={t("Some businesses offer special priced packages for multiple sessions")}
								viewPackage={true}
							/>
						)
					)}
				</div>
				)
			)}

			<DialogText
				onOpen={openPixConfirmation}
				message={t("Pix payment confirmed!")}
				title=" "
				pritman={true}
				onClose={async () => {
					setOpenPixConfirmation(false);
					loadDetails(pixConfirmation);
					setPixConfirmation(null);
				}}></DialogText>

			<ModalVerifyAccess
				open={false}
				onClose={() => setShowVerifyAccessModal(false)}
			/>
		</>
	);
};

export default withRouter(CardReservations);
